import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, AlertTriangle, Phone, Download, Users, Search } from 'lucide-react';
import { getBandwidthNumbers, syncBandwidthNumbers, inspectBandwidthNumbers } from '../services/portalApi';

export default function BandwidthNumbers({ user, viewAsCustomer }) {
  const isAdmin = Boolean(user?.isAdmin);

  const [numbers, setNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error,   setError]   = useState(null);
  const [search,  setSearch]  = useState('');
  const [inspecting, setInspecting] = useState(false);
  const [inspection, setInspection] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = isAdmin && viewAsCustomer ? { customerId: viewAsCustomer.id } : {};
      const data = await getBandwidthNumbers(params);
      setNumbers(data.numbers || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, viewAsCustomer]);

  useEffect(() => { load(); }, [load]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await syncBandwidthNumbers();
      await load();
      const errNote = r.errors?.length ? `\n\n${r.errors.length} warning(s) — check server logs.` : '';
      alert(
        `Synced ${r.total} number${r.total !== 1 ? 's' : ''} across ${r.sites} sub-account${r.sites !== 1 ? 's' : ''}.\n` +
        `Mapped to a customer: ${r.mappedToCustomers}\nUnassigned: ${r.unassigned}${errNote}`
      );
    } catch (e) {
      alert('Sync failed: ' + e.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleInspect = async () => {
    setInspecting(true);
    setInspection(null);
    try {
      const r = await inspectBandwidthNumbers();
      setInspection(r);
    } catch (e) {
      setInspection({ error: e.message });
    } finally {
      setInspecting(false);
    }
  };

  const filtered = search
    ? numbers.filter(n =>
        n.phone_number.includes(search) ||
        n.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
        n.sub_account_name?.toLowerCase().includes(search.toLowerCase())
      )
    : numbers;

  // Group by customer (Unassigned last).
  const grouped = {};
  for (const n of filtered) {
    const key = n.customer_name || 'Unassigned';
    (grouped[key] ||= []).push(n);
  }
  const groupKeys = Object.keys(grouped).sort((a, b) =>
    a === 'Unassigned' ? 1 : b === 'Unassigned' ? -1 : a.localeCompare(b));

  const mapped        = numbers.filter(n => n.customer_id).length;
  const unassigned    = numbers.length - mapped;
  const customerCount = new Set(numbers.filter(n => n.customer_id).map(n => n.customer_id)).size;

  const fmtDate = (s) => s ? String(s).replace('T', ' ').slice(0, 16) : '—';

  return (
    <div className="content">
      {/* Stats */}
      <div className="stats-grid mb-20" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
        {[
          { label: 'Total Numbers', value: numbers.length, color: 'var(--text-2)' },
          { label: 'Customers',     value: customerCount,  color: 'var(--info)' },
          { label: 'Mapped',        value: mapped,         color: 'var(--ok)' },
          { label: 'Unassigned',    value: unassigned,     color: 'var(--warn)' },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={{ color: s.color }}>{loading ? '…' : s.value}</div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Phone size={15} color="var(--info)"/>
            <div className="card-title">Bandwidth Number Inventory</div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Search number, customer or sub-account…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                background: 'var(--bg)', border: '1px solid var(--border-strong)', borderRadius: 6,
                color: 'var(--text-2)', padding: '5px 10px', fontSize: 12, width: 240,
              }}
            />
            {isAdmin && (
              <button className="btn btn-sm" onClick={handleInspect} disabled={inspecting}
                title="Dry run: show what the Bandwidth API returns without writing anything">
                <Search size={11} style={inspecting ? { animation: 'spin 0.8s linear infinite' } : {}}/>
                {' '}{inspecting ? 'Inspecting…' : 'Inspect (dry-run)'}
              </button>
            )}
            {isAdmin && (
              <button className="btn btn-sm" onClick={handleSync} disabled={syncing || loading}
                title="Pull the number inventory from the Bandwidth Dashboard API, grouped per sub-account">
                <Download size={11} style={syncing ? { animation: 'spin 0.8s linear infinite' } : {}}/>
                {' '}{syncing ? 'Syncing…' : 'Sync from Bandwidth'}
              </button>
            )}
            <button className="btn btn-sm" onClick={load} disabled={loading}>
              <RefreshCw size={11}/>
            </button>
          </div>
        </div>

        {error && (
          <div style={{ padding: '12px 20px', display: 'flex', gap: 8, alignItems: 'center', color: 'var(--err)', fontSize: 13 }}>
            <AlertTriangle size={14}/> {error}
          </div>
        )}

        {inspection && (
          <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--info)' }}>
                Dry-run result — nothing was written. Copy this and share it for review.
              </span>
              <button className="btn btn-sm" onClick={() => setInspection(null)}>Dismiss</button>
            </div>
            <pre style={{
              margin: 0, maxHeight: 360, overflow: 'auto',
              background: 'var(--bg)', border: '1px solid var(--border-strong)', borderRadius: 6,
              padding: 12, fontSize: 11, lineHeight: 1.5, color: 'var(--text-2)', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>
              {JSON.stringify(inspection, null, 2)}
            </pre>
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', color: 'var(--muted-3)', padding: '40px 0' }}>Loading…</div>
        ) : numbers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '50px 0' }}>
            <div style={{ color: 'var(--muted-3)', fontSize: 13, marginBottom: 8 }}>No numbers yet.</div>
            {isAdmin && (
              <div style={{ color: 'var(--muted-3)', fontSize: 12 }}>
                Click <strong style={{ color: 'var(--info)' }}>Sync from Bandwidth</strong> to pull the number inventory.
              </div>
            )}
          </div>
        ) : (
          groupKeys.map(customer => (
            <div key={customer}>
              <div style={{
                padding: '8px 20px', background: 'var(--surface-2)',
                borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)',
                fontSize: 11, fontWeight: 600,
                color: customer === 'Unassigned' ? 'var(--warn)' : 'var(--info)',
                textTransform: 'uppercase', letterSpacing: '0.06em',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <Users size={11}/> {customer}
                <span style={{ color: 'var(--muted-3)', fontWeight: 400, textTransform: 'none' }}>({grouped[customer].length})</span>
              </div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Phone Number</th>
                      <th>Sub-account</th>
                      <th>Status</th>
                      <th>Last Seen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grouped[customer].map(n => (
                      <tr key={n.phone_number}>
                        <td>
                          <span className="tag font-mono" style={{ fontSize: 13 }}>{n.phone_number}</span>
                        </td>
                        <td style={{ color: 'var(--muted)', fontSize: 12 }}>
                          {n.sub_account_name || <span style={{ color: 'var(--muted-3)', fontStyle: 'italic' }}>—</span>}
                          {n.sub_account_id ? <span style={{ color: 'var(--muted-3)' }}> ({n.sub_account_id})</span> : null}
                        </td>
                        <td>
                          <span style={{
                            fontSize: 11, padding: '2px 8px', borderRadius: 10,
                            background: n.status === 'in-service' ? 'var(--ok-bg)' : 'var(--warn-bg)',
                            color: n.status === 'in-service' ? 'var(--ok)' : 'var(--warn)',
                            border: `1px solid ${n.status === 'in-service' ? 'var(--ok-border)' : 'var(--warn-border)'}`,
                          }}>
                            {n.status}
                          </span>
                        </td>
                        <td style={{ color: 'var(--muted-3)', fontSize: 11, fontFamily: 'monospace' }}>{fmtDate(n.last_seen)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
